import * as Sentry from '@sentry/browser';

/**
 * Captures an exception and associates it with custom tags and extra data in Sentry.
 *
 * @param e - The error object to be captured.
 * @param tags - An optional object containing key-value pairs to be added as tags in Sentry.
 * @param extras - An optional object containing key-value pairs to be added as extra data in Sentry.
 * @returns A promise that resolves to the Sentry event ID of the captured exception.
 */
export const captureExceptionWithTags = (
  e: Error,
  tags: { [tagName: string]: string } = {},
  extras: { [fieldName: string]: string } = {}
): Promise<string> => {
  return new Promise(resolve => {
    Sentry.withScope(scope => {
      try {
        Object.keys(tags).forEach(key => {
          scope.setTag(key, tags[key]);
        });
        Object.keys(extras).forEach(field => {
          scope.setExtra(field, extras[field]);
        });
        const id = Sentry.captureException(e);
        resolve(id);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to capture exception with Sentry:', error);
        // resolve blank string so we don't show to the user
        resolve('');
      }
    });
  });
};

/**
 * Captures a message with associated tags and extras using Sentry.
 *
 * @param m - The message to be captured.
 * @param tags - An optional object containing key-value pairs to be set as tags in the Sentry scope.
 * @param extras - An optional object containing key-value pairs to be set as extra data in the Sentry scope.
 * @returns A promise that resolves to the Sentry ID of the captured message.
 */
export const captureMessageWithTags = (
  m: string,
  tags: { [tagName: string]: string } = {},
  extras: { [fieldName: string]: string } = {}
): Promise<string> => {
  return new Promise(resolve => {
    Sentry.withScope(scope => {
      try {
        Object.keys(tags).forEach(key => {
          scope.setTag(key, tags[key]);
        });
        Object.keys(extras).forEach(field => {
          scope.setExtra(field, extras[field]);
        });
        const id = Sentry.captureMessage(m);
        resolve(id);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to capture message with Sentry:', error);
        // resolve blank string so we don't show to the user
        resolve('');
      }
    });
  });
};

/**
 * Captures a message in Sentry for a given HTTP response and groups it by message and status fingerprint.
 *
 * @param response - The HTTP response object to extract status and body from.
 * @param message - A descriptive message to log alongside the response details.
 * @returns promise that resolves when the message is captured.
 * @remarks
 * - The response body is included as an extra field in the Sentry scope for
 *   debugging purposes.
 * - The fingerprint is set to group messages by the combination of the
 *   provided message and the response status.
 */
export async function captureMessageForResponse(
  response: Response,
  message: string
) {
  const { status } = response;
  let body;
  try {
    body = await response.text();
  } catch {
    body = '';
  }

  Sentry.withScope(scope => {
    try {
      // Error body is extremely useful to be able to diagnose a problem. We don't
      // want it in the message because we don't want slightly different responses
      // to break grouping.
      scope.setExtra('response', body);
      const messageWithStatus = `${message}: status ${status}`;
      // By default, Sentry groups similar messages (e.g. when only the status changes).
      // Disable that for this, we want to group by status.
      scope.setFingerprint([messageWithStatus]);
      // We tried captureException here to have a file path, but the exception
      // stack trace is weird because this is in an async function.
      Sentry.captureMessage(messageWithStatus);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to capture message with Sentry:', error);
    }
  });
}
